import { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { FunctionsOutlined } from '@mui/icons-material';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    Typography,
    Box,
} from '@mui/material';
import { FilterCategory } from 'models/enums';
import Button from 'domains/core/components/Button';
import TextField from 'domains/core/components/TextField';
import { Value } from 'domains/core/components/Select/Select';
import AutocompleteInput from 'domains/segments/pages/SegmentCreatePage/Filter/FilterValueInput/AutocompleteInput/AutocompleteInput';
import Filter from './Filter/Filter';
import { SegmentFilterCategorySpecificField, SegmentFilterState } from 'domains/segments/types';
import { isConditionsFiltersValid } from './utils';
import { getDemographicFilters, getSegmentDefinition, getEngagementNameToValuesRef } from 'domains/segments/utils';
import useSegmentFilters from 'hooks/queries/useSegmentFilters';
import useSends from 'hooks/queries/useSends';
import useDynamicContentCategories from 'hooks/queries/useDynamicContentCategories';
import { RowDisplayCondition } from 'hooks/queries/useDisplayConditions';
import useCreateDisplayCondition from 'hooks/mutations/useCreateDisplayCondition';
import QueryKeys from 'hooks/queries/keys';
import theme from 'theme';

/**
 * Generates a human-readable description string from the filters array
 */
const generateFilterDescription = (
    filters: SegmentFilterState<SegmentFilterCategorySpecificField>[],
    matchType: string = 'AND'
): string => {
    if (!filters || filters.length === 0) {
        return 'No filters applied';
    }

    const filterDescriptions = filters.map((filter) => {
        const { category, name, operator, categorySpecificFields, autocompleteValue } = filter;

        // Helper function to safely get label from autocompleteValue
        const getAutocompleteLabel = (value?: any): string => {
            if (!value) return '';
            if (Array.isArray(value)) {
                return value.length > 0 && value[0]?.label ? value[0].label : '';
            }
            return value?.label || '';
        };

        // Helper function to get channel display name
        const getChannelDisplayName = (channel?: string): string => {
            if (!channel) return '';
            const channelLabels: { [key: string]: string } = {
                EMAIL: 'email',
                SMS: 'text message',
                DIRECTMAIL: 'direct mail',
                VOICE: 'voice call',
                NOOP: 'no operation',
            };
            return channelLabels[channel] || channel.toLowerCase();
        };

        switch (category) {
            case 'contact details': {
                const value = categorySpecificFields.value || getAutocompleteLabel(autocompleteValue);
                return `contact details: where ${name} ${operator} ${value}`;
            }

            case 'contact activity': {
                const sendLabel = getAutocompleteLabel(autocompleteValue);
                // Type guard and cast for engagement fields
                const engagementFields = categorySpecificFields as any;

                // Get channel information
                const channel = engagementFields?.channel;
                const channelDisplayName = getChannelDisplayName(channel);

                // Get interaction value information from sendInteractionField
                const sendInteractionField = engagementFields?.sendInteractionField;

                // Extract the interaction value from sendInteractionField.autocompleteValue
                // This can be either a single object or an array of objects
                let interactionValue = '';
                if (sendInteractionField?.autocompleteValue) {
                    if (Array.isArray(sendInteractionField.autocompleteValue)) {
                        // Multi-select case - get the first value's label
                        interactionValue =
                            sendInteractionField.autocompleteValue.length > 0
                                ? sendInteractionField.autocompleteValue[0]?.label || ''
                                : '';
                    } else {
                        // Single select case - get the label directly
                        interactionValue = sendInteractionField.autocompleteValue?.label || '';
                    }
                }

                // Fallback to legacy segmentLinkInput for backward compatibility
                const linkValue = engagementFields?.segmentLinkInput?.value || '';

                // Build the description based on available information
                let description = `contact activity: where receiver ${operator} the send '${sendLabel}'`;

                // Add channel information if available
                if (channelDisplayName) {
                    description += ` via ${channelDisplayName}`;
                }

                // Special case for SMS reply - include what was replied with
                if (
                    (channel === 'SMS' || channelDisplayName === 'text message') &&
                    operator &&
                    operator.toLowerCase().includes('reply') &&
                    interactionValue
                ) {
                    description += ` with reply content '${interactionValue}'`;
                }
                // Add interaction value if available (prioritize sendInteractionField over legacy linkValue)
                else if (interactionValue) {
                    description += ` with interaction '${interactionValue}'`;
                } else if (linkValue) {
                    description += ` with value '${linkValue}'`;
                }

                return description;
            }

            case 'contact events': {
                // Type guard and cast for event fields
                const eventFields = categorySpecificFields as any;
                const conditions = eventFields?.conditions || [];

                if (conditions.length > 0) {
                    const conditionDescriptions = conditions
                        .map((condition: any) => {
                            if (condition.name === 'end' && condition.operator === 'within' && condition.value) {
                                const startDate = new Date(condition.value.start).toLocaleDateString();
                                const endDate = new Date(condition.value.end).toLocaleDateString();
                                return `${condition.name} ${condition.operator} ${startDate} and ${endDate}`;
                            } else {
                                return `${condition.name} ${condition.operator} ${condition.value}`;
                            }
                        })
                        .join(' and ');

                    return `contact events: ${operator} ${name} with ${conditionDescriptions}`;
                } else {
                    const value = eventFields?.value || '';
                    return `contact events: ${operator} ${name}${value ? ` with value ${value}` : ''}`;
                }
            }

            default:
                return `${category}: ${name} ${operator} ${categorySpecificFields.value || ''}`;
        }
    });

    // Join all filter descriptions with the match type
    const connector = ` ${matchType.toUpperCase()} `;
    return filterDescriptions.join(connector);
};

type DateTimeErrors = { [key: string]: boolean };

type ConditionsBuilderFormInputs = {
    filters: SegmentFilterState<SegmentFilterCategorySpecificField>[];
    matchType: string;
    name: string;
    category: string;
    description: string;
};

export type Props = {
    isDialogVisible: boolean;
    setIsDialogVisible: (isDialogVisible: boolean) => void;
    onCreateConditionSuccess?: () => void;
    onCreateConditionError?: (error: Error) => void;
};

export const MAX_FILTER_LENGTH = 20;

const ConditionsBuilderDialog = ({
    isDialogVisible,
    setIsDialogVisible,
    onCreateConditionSuccess,
    onCreateConditionError,
}: Props) => {
    const [category, setCategory] = useState<FilterCategory>();
    const [datetimeErrors, setDatetimeErrors] = useState<DateTimeErrors>({});
    const [nextFilterId, setNextFilterId] = useState(0);
    const [valuesRef, setValuesRef] = useState('');

    const queryClient = useQueryClient();

    const { data: segmentFilters, isSuccess: isSegmentFiltersSuccess } = useSegmentFilters({
        version: 3,
    });

    // Fetch dynamic content categories from API
    const { data: categoriesData, isSuccess: isCategoriesSuccess } = useDynamicContentCategories();

    // Fetch sends data for contact activity filters
    const { data: sends } = useSends({
        path: valuesRef,
        reactQueryOptions: { enabled: Boolean(valuesRef) },
    });

    // Set up the display condition creation mutation
    const { mutate: createDisplayCondition, isLoading: isCreatingCondition } = useCreateDisplayCondition({
        onSuccess: (newCondition: RowDisplayCondition) => {
            // Get current row display conditions data
            const currentConditions =
                queryClient.getQueryData<RowDisplayCondition[]>(QueryKeys.ROW_DISPLAY_CONDITIONS) || [];

            // Append the new condition to the existing data
            const updatedConditions = [...currentConditions, newCondition];

            // Update the query data with the new list
            queryClient.setQueryData(QueryKeys.ROW_DISPLAY_CONDITIONS, updatedConditions);

            // Close dialog on successful creation
            handleClose();

            // Call parent success callback
            onCreateConditionSuccess?.();
        },
        onError: (error) => {
            // Call parent error callback
            onCreateConditionError?.(error);
        },
    });

    const categoryOptions: Value[] = useMemo(() => {
        if (!isCategoriesSuccess || !categoriesData) {
            return [];
        }

        return categoriesData.map((category: string) => ({
            label: category,
            value: category,
        }));
    }, [categoriesData, isCategoriesSuccess]);

    const {
        control,
        formState: { isValid },
        watch,
        reset,
        setValue,
    } = useForm<ConditionsBuilderFormInputs>({
        mode: 'onChange',
        defaultValues: {
            filters: [],
            matchType: 'AND',
            name: '',
            category: '',
            description: '',
        },
    });

    const formData = watch();
    const { filters: stateFilters, name: stateName, description: stateDescription, category: stateCategory } = formData;

    // Always use 'All Contacts' for record types
    const hasAllContacts = true;
    const recordTypes: string[] = useMemo((): string[] => [], []); // Empty array since hasAllContacts is true
    const segmentRecordTypes = segmentFilters?.demographic?.recordTypes;

    const demographicFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        const result = getDemographicFilters({ hasAllContacts, recordTypes, segmentRecordTypes });
        return result;
    }, [segmentFilters, hasAllContacts, recordTypes, segmentRecordTypes]);

    const engagementFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        // RecordTypes[0] represents engagement filters because there's a single item and its recordType value is "" from the API
        return segmentFilters.engagement.recordTypes[0].filters;
    }, [segmentFilters]);

    const eventRecordTypeFilters = useMemo(() => {
        if (!segmentFilters) {
            return [];
        }
        return segmentFilters.contactEvent.recordTypes;
    }, [segmentFilters]);

    const engagementNameToValuesRef = useMemo(() => getEngagementNameToValuesRef(engagementFilters), [
        engagementFilters,
    ]);

    // Do no call these functions directly, instead call append, etc below that also reset the preview.
    const { fields, append: appendAction, remove: removeAction, update: updateAction } = useFieldArray({
        keyName: 'key',
        name: 'filters',
        control,
    });

    const datetimeError = Object.values(datetimeErrors).includes(true);

    const handleDatetimeError = (filterId: string, error: boolean) =>
        setDatetimeErrors({ ...datetimeErrors, [filterId]: error });

    const handleClose = () => {
        reset();
        setIsDialogVisible(false);
        setNextFilterId(0);
        setDatetimeErrors({});
        setCategory(undefined);
    };

    const handleCreate = () => {
        // Get segment definition similar to saveAudience in SegmentCreatePage
        const getConditionSegment = () =>
            getSegmentDefinition({
                filters: stateFilters,
                hasAllContacts,
                matchType: formData.matchType as 'AND' | 'OR',
                recordTypes, // Empty array since hasAllContacts is true
            });

        const conditionDefinition = getConditionSegment();

        // Create the API payload with the required modifications
        const apiPayload = {
            label: formData.name,
            category: formData.category,
            description: formData.description,
            condition: {
                matchType: conditionDefinition.matchType,
                contactEvent: conditionDefinition.contactEvent,
                demographic: conditionDefinition.demographic,
                engagement: conditionDefinition.engagement,
                // Note: contactRecordType is excluded since hasAllContacts is true
            },
        };

        // Make the actual API call
        createDisplayCondition(apiPayload);
    };

    const updateFilter = (index: number, filter: SegmentFilterState<SegmentFilterCategorySpecificField>) => {
        updateAction(index, filter);

        if (filter.category === FilterCategory.CONTACT_ACTIVITY && filter.name) {
            setValuesRef(engagementNameToValuesRef[filter.name]);
        }
        if (fields.length === 1) {
            setCategory(undefined);
        } else {
            if (category === undefined) {
                const newCategory = filter.category !== undefined ? filter.category : undefined;
                setCategory(newCategory);
            }
        }
    };

    const removeFilter = (index: number) => {
        removeAction(index);

        // Length will be 1 after remove, so category is set to nothing so user can freely choose
        if (fields.length === 2) {
            setCategory(undefined);
        }
        setNextFilterId((id) => id - 1);
    };

    const handleAddFilter = useCallback(() => {
        const addFilter = (id: number) => {
            const filterLength = stateFilters?.length - 1;
            const lastFilterCategory = stateFilters[filterLength]?.category;

            const append = (value: Parameters<typeof appendAction>[0]) => {
                appendAction(value);
            };

            if (fields.length === 1) {
                setCategory(lastFilterCategory);
            }
            append({
                id,
                category: lastFilterCategory,
                categorySpecificFields: { value: '' },
                name: '',
                operator: undefined,
            });
        };
        addFilter(nextFilterId);
        setNextFilterId((id) => id + 1);
    }, [appendAction, fields.length, nextFilterId, stateFilters]);

    const isFiltersEmpty = !stateFilters?.length;

    useEffect(() => {
        // Add a filter by default when dialog opens
        if (isDialogVisible && isFiltersEmpty) {
            handleAddFilter();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDialogVisible]);

    // Auto-update description based on filters
    useEffect(() => {
        if (stateFilters?.length > 0) {
            const filterDescription = generateFilterDescription(stateFilters, formData.matchType);

            // Only update description if it doesn't contain undefined or null values
            if (!filterDescription.includes('undefined') && !filterDescription.includes('null')) {
                setValue('description', filterDescription);
            }
        } else {
            // Clear description when no filters exist
            setValue('description', '');
        }
    }, [stateFilters, formData.matchType, setValue]);

    const hasInvalidFilters = !isConditionsFiltersValid(stateFilters, false); // Always pass false since we always have 'All Contacts'
    const isMaxFilterLimit = fields.length === MAX_FILTER_LENGTH;
    const isCreateDisabled =
        datetimeError ||
        hasInvalidFilters ||
        !isValid ||
        !stateName ||
        !stateDescription ||
        !stateCategory ||
        isCreatingCondition;
    return (
        <Dialog maxWidth="lg" fullWidth open={isDialogVisible} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems="center" direction="row" spacing={1}>
                    <FunctionsOutlined sx={{ paddingTop: '4px' }} />
                    <Typography variant="h6">create display condition</Typography>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ padding: '16px 32px' }}>
                <DialogContentText mb={3} mt={1}>
                    Show content only to the right audience—add filters below to create a dynamic content display
                    condition
                </DialogContentText>

                <Stack spacing={3}>
                    {/* Name and Category inputs side by side */}
                    <Stack direction="row" spacing={2}>
                        <Controller
                            name="name"
                            control={control}
                            defaultValue=""
                            rules={{
                                required: 'This field is required',
                                minLength: { value: 3, message: 'name must have at least 3 characters' },
                                maxLength: { value: 50, message: 'name must be under 50 characters' },
                            }}
                            render={({ field: { onChange, value, onBlur }, fieldState: { error, invalid } }) => (
                                <TextField
                                    required
                                    label="Name"
                                    value={value}
                                    onChange={onChange}
                                    onBlur={onBlur}
                                    error={invalid}
                                    helperText={invalid ? error.message : null}
                                    width="24rem"
                                />
                            )}
                        />

                        <Controller
                            control={control}
                            name="category"
                            defaultValue=""
                            rules={{
                                required: 'Please select or enter a category',
                                validate: (value) => {
                                    if (!value || value.trim() === '') {
                                        return 'Category cannot be empty';
                                    }
                                    return true;
                                },
                            }}
                            render={({ field: { onChange, value }, fieldState: { error, invalid } }) => (
                                <AutocompleteInput
                                    autocompleteInputValue={value || ''}
                                    autocompleteValue={value ? { label: value, value: value } : null}
                                    freeSolo={true}
                                    label="Category"
                                    multiple={false}
                                    onChange={(selectedValue) => {
                                        const newValue = selectedValue?.value || '';
                                        onChange(newValue);
                                    }}
                                    onInputChange={(inputValue) => {
                                        onChange(inputValue);
                                    }}
                                    options={categoryOptions}
                                    placeholderName="Category"
                                    type="string"
                                    error={invalid}
                                    helperText={invalid ? error.message : null}
                                    styles={{
                                        sxAutocomplete: { width: '24rem' },
                                    }}
                                />
                            )}
                        />
                    </Stack>

                    {/* Description input */}
                    <Controller
                        name="description"
                        control={control}
                        defaultValue=""
                        rules={{
                            required: 'This field is required',
                            minLength: { value: 3, message: 'description must have at least 3 characters' },
                            maxLength: { value: 800, message: 'description must be under 800 characters' },
                        }}
                        render={({ field: { onChange, value, onBlur }, fieldState: { error, invalid } }) => (
                            <TextField
                                required
                                label="Description"
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                                error={invalid}
                                helperText={invalid ? error.message : 'Auto-generated based on filters'}
                                multiline
                                rows={2}
                                width="49rem"
                                disabled={true}
                            />
                        )}
                    />

                    {/* Filter builder section */}
                    <Stack spacing={2}>
                        <Typography variant="body2" color="text.secondary">
                            Filter
                        </Typography>

                        {isSegmentFiltersSuccess && (
                            <Controller
                                control={control}
                                name="matchType"
                                render={({ field: { onChange, value } }) => {
                                    const handleChange = (e: any) => {
                                        onChange(e.target.value);
                                    };
                                    return (
                                        <>
                                            {stateFilters.map((filter, index) => (
                                                <Filter
                                                    key={`${filter.category}-${filter.id}`}
                                                    demographicFilters={demographicFilters}
                                                    engagementFilters={engagementFilters}
                                                    eventRecordTypeFilters={eventRecordTypeFilters}
                                                    filter={filter}
                                                    index={index}
                                                    sends={sends || []}
                                                    value={value}
                                                    handleChange={handleChange}
                                                    handleDatetimeError={handleDatetimeError}
                                                    hideMatchType={index === fields.length - 1}
                                                    removeFilter={removeFilter}
                                                    updateFilter={updateFilter}
                                                />
                                            ))}
                                        </>
                                    );
                                }}
                            />
                        )}

                        <Box>
                            <Button
                                disabled={isMaxFilterLimit}
                                variant="contained"
                                onClick={handleAddFilter}
                                size="medium"
                                sx={{
                                    background: theme.palette.action.active,
                                    '&:hover': {
                                        background: theme.palette.action.selected,
                                    },
                                }}
                            >
                                Add Filter
                            </Button>
                        </Box>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={handleClose}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleCreate} disabled={isCreateDisabled}>
                    {isCreatingCondition ? 'Creating...' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConditionsBuilderDialog;
